/**
 * The theme, applied to the document rather than by a component.
 *
 * It used to be applied inside `ThemeToggle`'s effect, which meant the theme existed only on
 * screens that happened to render the toggle. The reader renders none — it is a deliberate escape
 * from the review chrome — so a note opened at night came up on white paper whatever the editor
 * had chosen, and the choice reappeared the moment they navigated back. Applying it here, before
 * React mounts, also removes the flash of the wrong paper on first paint.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'pressentia-theme';

export function readTheme(): Theme {
  const stored = safeRead();
  if (stored === 'light' || stored === 'dark') return stored;
  // No choice recorded means follow the machine. `matchMedia` is guarded because this module runs
  // before anything else and a missing API here would take down the whole bundle.
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // A locked-down browser can refuse storage. The theme still applies for this visit, which is
    // the part the editor asked for.
  }
}

/** Run once, before the first paint. */
export function installTheme(): void {
  document.documentElement.dataset['theme'] = readTheme();
}

function safeRead(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
